import { useEffect, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import { useDownloadInfo } from "../utils/storeLinks";
import AssistantChatPreview from "../components/AssistantChatPreview";
import { ClaudeLogo, ChatGPTLogo } from "../components/AssistantLogos";
import cinnamonSource from "../images/feature-cinnamon-source.jpg";
import cinnamonRecipe from "../images/feature-cinnamon-recipe.jpg";
import curvedArrowIcon from "../icons/curved-arrow-transparent.png";
import pancakes from "../images/food/pancakes.jpg";
import bread from "../images/food/bread.jpg";
import avocadoToast from "../images/food/avocado-toast.jpg";
import pasta from "../images/food/pasta.jpg";
import cookies from "../images/food/cookies.jpg";
import chocolate from "../images/food/chocolate.jpg";
import smoothies from "../images/food/smoothies.jpg";
import cafePastries from "../images/food/cafe-pastries.jpg";
import greenJuice from "../images/food/green-juice.jpg";
import burgers from "../images/food/burgers.jpg";
import blueberryMuffins from "../images/food/blueberry-muffins.jpg";
import creatorJessica from "../images/creator-jessica.png";
import creatorMarco from "../images/creator-marco.png";
import creatorDaniel from "../images/creator-daniel.png";
import creatorRachel from "../images/creator-rachel.png";
import creatorOlivia from "../images/creator-olivia.png";
import facebookIcon from "../icons/sources/facebook.svg";
import instagramAppIcon from "../icons/sources/instagram-app.jpg";
import tiktokAppIcon from "../icons/sources/tiktok-app.jpg";
import whatsappAppIcon from "../icons/sources/whatsapp-app.jpg";
import safariAppIcon from "../icons/sources/safari-app.png";
import notesAppIcon from "../icons/sources/notes-app.png";

const sourceIcons = [
  { label: "Instagram", icon: instagramAppIcon, appIcon: true },
  { label: "TikTok", icon: tiktokAppIcon, appIcon: true },
  { label: "Facebook", icon: facebookIcon, color: "#1877f2" },
  { label: "WhatsApp", icon: whatsappAppIcon, appIcon: true },
  { label: "Safari", icon: safariAppIcon, appIcon: true, size: 36 },
  { label: "Notes", icon: notesAppIcon, appIcon: true },
];

const recipeCards = [
  {
    title: "Avocado toast",
    image: avocadoToast,
    area: "salad",
    position: "50% 55%",
  },
  {
    title: "Strawberry pancakes",
    image: pancakes,
    area: "pancakes",
    position: "50% 55%",
  },
  {
    title: "Sugar cookies",
    image: cookies,
    area: "muffins",
    position: "50% 72%",
  },
  {
    title: "Fresh bread with honey",
    image: bread,
    area: "steak",
    position: "50% 50%",
  },
  {
    title: "Tomato pasta",
    image: pasta,
    area: "details",
    position: "50% 45%",
  },
  {
    title: "Fresh fruit smoothies",
    image: smoothies,
    area: "feed",
    position: "50% 62%",
  },
  {
    title: "Chocolate bars",
    image: chocolate,
    area: "juice",
    position: "50% 68%",
  },
  {
    title: "Cafe pastries",
    image: cafePastries,
    area: "cafe",
    position: "50% 62%",
  },
  {
    title: "Mint green juice",
    image: greenJuice,
    area: "dessert",
    position: "50% 58%",
  },
];

const collectionGroups = [
  {
    title: "Weeknight Dinners",
    count: "24 recipes",
    images: [
      { src: pasta },
      { src: avocadoToast },
      { src: burgers, position: "52% 58%" },
    ],
  },
  {
    title: "Sweet Tooth",
    count: "18 recipes",
    images: [
      { src: blueberryMuffins, position: "50% 58%" },
      { src: chocolate },
      { src: cinnamonSource, position: "48% 52%" },
    ],
  },
  {
    title: "Fresh & Healthy",
    count: "16 recipes",
    images: [
      { src: smoothies },
      { src: greenJuice },
      { src: avocadoToast },
    ],
  },
  {
    title: "Weekend Brunch",
    count: "12 recipes",
    images: [
      { src: cafePastries },
      { src: pancakes },
      { src: bread },
    ],
  },
];

const communityRecipes = [
  {
    title: "Spicy Vodka Pasta",
    image: pasta,
    position: "50% 54%",
    creator: "Jessica",
    avatar: creatorJessica,
    activity: "84 cooked · 326 saves",
  },
  {
    title: "Avocado Toast",
    image: avocadoToast,
    position: "50% 58%",
    creator: "Daniel",
    avatar: creatorDaniel,
  },
  {
    title: "Sunday Pancakes",
    image: pancakes,
    position: "50% 55%",
    creator: "Olivia",
    avatar: creatorOlivia,
  },
];

const communityActivity = [
  { name: "Rachel", action: "saved this", avatar: creatorRachel },
  { name: "Marco", action: "cooked this", avatar: creatorMarco },
  { name: "Olivia", action: "+ Follow", avatar: creatorOlivia },
];

const appStoreReviews = [
  {
    quote:
      "I love that my screen stays on while I’m looking at a recipe. Such a small thing but SO helpful when I’m cooking.",
    name: "Maya",
    location: "California",
  },
  {
    quote:
      "Finally all the recipes I save from Instagram and TikTok are actually organized somewhere I can find them.",
    name: "Emma",
    location: "United Kingdom",
  },
  {
    quote:
      "This is basically the recipe app I’ve always wanted. Simple, beautiful and actually useful.",
    name: "Nina",
    location: "Texas",
  },
  {
    quote:
      "I used to have recipes saved everywhere—screenshots, Instagram, Notes, random websites. Now everything is in Bite.",
    name: "Alex",
    location: "Canada",
  },
  {
    quote:
      "The collections are my favorite part. I have separate ones for weeknight dinners, baking, holidays and recipes I want to try.",
    name: "Priya",
    location: "New York",
  },
  {
    quote: "Dark mode is so good. The whole app feels really clean and easy to use.",
    name: "Jordan",
    location: "Australia",
  },
  {
    quote:
      "Being able to add my own notes to recipes is amazing. I never remember what I changed the last time I made something.",
    name: "Lauren",
    location: "Florida",
  },
  {
    quote:
      "I love seeing what my friends are saving and cooking. I’ve already found so many recipes I want to make.",
    name: "Sam",
    location: "United Kingdom",
  },
  {
    quote:
      "I can send a recipe to Bite and it turns it into an actual recipe instead of having to go back through a video every time.",
    name: "Sarah",
    location: "Washington",
  },
  {
    quote:
      "Love that I can make a recipe collection and share it with my family. We finally have all our favorite recipes in one place.",
    name: "Chris",
    location: "Canada",
  },
];

const everydayFeatures = [
  {
    title: "Meal planning",
    body: "Plan your week so dinner is never a last-minute decision.",
  },
  {
    title: "Shopping lists",
    body: "Pull ingredients from your recipes into one shopping list.",
  },
  {
    title: "Recipe notes",
    body: "Save substitutions, changes and reminders for next time.",
  },
  {
    title: "Screen stays on",
    body: "Your screen stays awake while you’re cooking.",
  },
  {
    title: "Dark mode",
    body: "A comfortable view for cooking after dark.",
  },
];

function ReviewCarousel({ reviews }) {
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const dragRef = useRef({
    active: false,
    startX: 0,
    startOffset: 0,
    moved: false,
  });
  const [paused, setPaused] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (reduceMotion) return undefined;

    const track = trackRef.current;
    if (!track) return undefined;

    let frameId = 0;
    let lastTime = performance.now();
    const speed = 110; // px/s — ~30s per loop for a typical track

    const tick = now => {
      const elapsed = now - lastTime;
      lastTime = now;

      if (!dragRef.current.active && !paused) {
        offsetRef.current -= (speed * elapsed) / 1000;
        const loopWidth = track.scrollWidth / 2;
        if (loopWidth > 0 && Math.abs(offsetRef.current) >= loopWidth) {
          offsetRef.current += loopWidth;
        }
        track.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
      }

      frameId = requestAnimationFrame(tick);
    };

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [paused, reduceMotion, reviews]);

  const onPointerDown = event => {
    if (reduceMotion) return;
    dragRef.current = {
      active: true,
      startX: event.clientX,
      startOffset: offsetRef.current,
      moved: false,
    };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const onPointerMove = event => {
    if (!dragRef.current.active || reduceMotion) return;
    const delta = event.clientX - dragRef.current.startX;
    if (Math.abs(delta) > 3) dragRef.current.moved = true;
    offsetRef.current = dragRef.current.startOffset + delta;

    const track = trackRef.current;
    if (!track) return;
    const loopWidth = track.scrollWidth / 2;
    if (loopWidth > 0) {
      while (offsetRef.current <= -loopWidth) offsetRef.current += loopWidth;
      while (offsetRef.current > 0) offsetRef.current -= loopWidth;
    }
    track.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
  };

  const onPointerUp = event => {
    if (!dragRef.current.active) return;
    dragRef.current.active = false;
    try {
      event.currentTarget.releasePointerCapture(event.pointerId);
    } catch {
      // Pointer may already be released.
    }
  };

  const loopReviews = reduceMotion ? reviews : [...reviews, ...reviews];

  return (
    <ReviewMarquee
      aria-label="Bite reviews"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <ReviewTrack
        ref={trackRef}
        $static={reduceMotion}
        onDragStart={event => event.preventDefault()}
      >
        {loopReviews.map((review, index) => (
          <ReviewCard key={`${review.name}-${index}`}>
            <Stars aria-label="5 stars">★★★★★</Stars>
            <blockquote>“{review.quote}”</blockquote>
            <figcaption>
              {review.name}
              <span aria-hidden="true"> · </span>
              {review.location}
            </figcaption>
          </ReviewCard>
        ))}
      </ReviewTrack>
    </ReviewMarquee>
  );
}

function Home() {
  const download = useDownloadInfo();

  useEffect(() => {
    const revealItems = document.querySelectorAll("[data-reveal]");
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
          } else {
            entry.target.classList.remove("is-visible");
          }
        });
      },
      { threshold: 0.14, rootMargin: "0px 0px -8%" }
    );

    revealItems.forEach(item => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <Page>
      <main>
        <HeroSection aria-labelledby="home-heading">
          <HeroCopy data-reveal data-reveal-direction="scale">
            <NewPill href="/connect">
              <b>New</b>
              Works with Claude &amp; ChatGPT
              <span aria-hidden="true">→</span>
            </NewPill>
            <h1 id="home-heading">
              All your recipes.
              <br />
              In one place.
            </h1>
            <HeroText>
              Save recipes from anywhere. Organize what you love.
              <br />
              Discover what to cook next.
            </HeroText>
            <CTAButton href={download.url} target="_blank" rel="noreferrer">
              {download.label}
            </CTAButton>
            <Proof>
              <Stars aria-label="5 stars">★★★★★</Stars>
              <span>Rated 5 stars on the App Store</span>
              <small>Join thousands already using Bite.</small>
            </Proof>
          </HeroCopy>

          <RecipeCollage aria-label="Recipe inspiration collage">
            {recipeCards.map((card, index) => (
              <FoodCard
                key={`${card.title}-${index}`}
                $area={card.area}
                data-reveal
                data-reveal-direction={index < 3 ? "left" : "right"}
                data-reveal-delay={index % 3}
              >
                <img
                  src={card.image}
                  alt={card.title}
                  style={{ objectPosition: card.position }}
                />
              </FoodCard>
            ))}
          </RecipeCollage>
        </HeroSection>

        <FeatureSection id="features" $wide>
          <FeatureCopy data-reveal data-reveal-direction="left">
            <Eyebrow>Save recipes from anywhere</Eyebrow>
            <h2>
              <span>See it. Save it.</span>
              <br />
              Cook it.
            </h2>
            <p>
              Save recipes from Instagram, TikTok, websites, photos and more.
              Bite turns them into clean, organized recipes ready when you
              are.
            </p>
            <SourceList aria-label="Supported recipe sources">
              {sourceIcons.map(source => (
                <SourceIcon
                  key={source.label}
                  role="img"
                  aria-label={source.label}
                  title={source.label}
                  tabIndex="0"
                >
                  {source.appIcon ? (
                    <SourceAppIcon
                      src={source.icon}
                      alt=""
                      $size={source.size}
                    />
                  ) : (
                    <SourceGlyph
                      aria-hidden="true"
                      $icon={source.icon}
                      $color={source.color}
                    />
                  )}
                </SourceIcon>
              ))}
            </SourceList>
          </FeatureCopy>
          <TransformVisual
            aria-label="Saved content becomes a Bite recipe"
            data-reveal
            data-reveal-direction="right"
            data-reveal-delay="1"
          >
            <SourceCard>
              <InstagramBadge aria-label="From Instagram" title="Instagram">
                <img src={instagramAppIcon} alt="" />
              </InstagramBadge>
              <img
                src={cinnamonSource}
                alt="Instagram post showing a cinnamon roll recipe"
              />
              <SourceDetails aria-hidden="true">
                <span />
                <span />
                <span />
              </SourceDetails>
            </SourceCard>
            <Arrow src={curvedArrowIcon} alt="" aria-hidden="true" />
            <RecipePreview>
              <img
                src={cinnamonRecipe}
                alt="Saving an Instagram recipe to the Bite recipe app"
              />
            </RecipePreview>
          </TransformVisual>
        </FeatureSection>

        <FeatureSection id="collections" $reverse>
          <CollectionGrid>
            {collectionGroups.map((collection, index) => (
              <CollectionCard
                key={collection.title}
                data-reveal
                data-reveal-direction="scale"
                data-reveal-delay={index % 3}
              >
                <CollectionImages
                  role="img"
                  aria-label={`${collection.title} recipe collection organized in Bite`}
                >
                    {collection.images.map((image, index) => (
                      <img
                        key={`${collection.title}-${image.src}`}
                        src={image.src}
                        style={{ objectPosition: image.position || "50% 50%" }}
                        alt=""
                      />
                  ))}
                </CollectionImages>
                <strong>{collection.title}</strong>
                <span>{collection.count}</span>
              </CollectionCard>
            ))}
          </CollectionGrid>
          <FeatureCopy
            data-reveal
            data-reveal-direction="right"
            data-reveal-delay="1"
          >
            <Eyebrow>Organize your recipes</Eyebrow>
            <h2>
              Everything you love,
              <br />
              organized.
            </h2>
            <p>
              Create collections for the way you cook. Weeknight dinners,
              desserts, recipes you want to try and anything else you love.
            </p>
          </FeatureCopy>
        </FeatureSection>

        <CommunitySection id="community">
          <CommunityCopy data-reveal data-reveal-direction="left">
            <Eyebrow>Community</Eyebrow>
            <h2>
              Join a community
              <br />
              of recipe lovers.
            </h2>
            <p>
              Follow friends and creators, discover new recipes, and see what
              others are cooking. Share your own recipes and collections, too.
            </p>
          </CommunityCopy>
          <CommunityGrid data-reveal data-community-scene>
            {communityRecipes.map((recipe, index) => (
              <CommunityCard key={recipe.title} $featured={index === 0}>
                <CommunityRecipeImage
                  $featured={index === 0}
                  src={recipe.image}
                  alt={`${recipe.title} shared on Bite`}
                  style={{ objectPosition: recipe.position }}
                />
                <CommunityCardBody $featured={index === 0}>
                  {recipe.creator && (
                    <Creator $featured={index === 0}>
                      <img src={recipe.avatar} alt="" />
                      <span>{recipe.creator}</span>
                    </Creator>
                  )}
                  <strong>{recipe.title}</strong>
                  {index === 0 && <small>{recipe.activity}</small>}
                </CommunityCardBody>
              </CommunityCard>
            ))}
            <CommunityActivity aria-label="Community recipe activity">
              {communityActivity.map((person, index) => (
                <ActivityBubble key={person.name} $index={index}>
                  <img src={person.avatar} alt="" />
                  <span>
                    <strong>{person.name}</strong>
                    {person.action}
                  </span>
                </ActivityBubble>
              ))}
            </CommunityActivity>
          </CommunityGrid>
        </CommunitySection>

        <MoreSection id="more" aria-labelledby="more-heading">
          <MoreHeader data-reveal>
            <Eyebrow>More to love</Eyebrow>
            <h2 id="more-heading">Built for everyday cooking.</h2>
            <p>
              Beyond saving and organizing. Thoughtful features that make Bite
              even better in the kitchen.
            </p>
          </MoreHeader>
          <MoreLayout>
            <AskBitePanel
              data-reveal
              data-reveal-direction="left"
              aria-label="Ask Bite"
            >
              <AskBiteCopy>
                <AskBiteLabel>Ask Bite</AskBiteLabel>
                <strong>Your recipe, with answers.</strong>
                <p>
                  Ask questions about the recipe you’re cooking. Get help with
                  substitutions, measurements, dietary changes and more.
                </p>
              </AskBiteCopy>
              <AskChat aria-hidden="true">
                <AskBubble $from="user">
                  I’m out of heavy cream. What can I substitute without
                  changing the texture?
                </AskBubble>
                <AskBubble $from="bite">
                  For this recipe, use full-fat coconut milk or half-and-half.
                  Half-and-half will keep the flavor closest to the original.
                </AskBubble>
              </AskChat>
            </AskBitePanel>
            <MoreFeatureList>
              {everydayFeatures.map((feature, index) => (
                <MoreFeature
                  key={feature.title}
                  data-reveal
                  data-reveal-direction="right"
                  data-reveal-delay={index % 3}
                >
                  <strong>{feature.title}</strong>
                  <span>{feature.body}</span>
                </MoreFeature>
              ))}
            </MoreFeatureList>
          </MoreLayout>
        </MoreSection>

        <FeatureSection id="ai" aria-labelledby="ai-heading">
          <FeatureCopy data-reveal data-reveal-direction="left">
            <AssistantBadges>
              <Eyebrow>Works with</Eyebrow>
              <span>
                <ClaudeLogo size={18} style={{ color: "#d97757" }} />
                Claude
              </span>
              <span>
                <ChatGPTLogo size={18} />
                ChatGPT
              </span>
            </AssistantBadges>
            <h2 id="ai-heading">
              Your recipes,
              <br />
              in every chat.
            </h2>
            <p>
              Connect Bite to the AI assistant you already use. Ask it to plan
              the week from your favorites, build your shopping list or save a
              recipe you just found, and it all lands right in Bite.
            </p>
            <AssistantLinks>
              <TextLink href="/connect">
                See how it works <span aria-hidden="true">→</span>
              </TextLink>
            </AssistantLinks>
          </FeatureCopy>
          <AssistantPreview
            data-reveal
            data-reveal-direction="right"
            data-reveal-delay="1"
          />
        </FeatureSection>

        <ReviewsSection aria-labelledby="reviews-heading">
          <ReviewsHeader data-reveal>
            <Eyebrow>Bite Reviews</Eyebrow>
            <h2 id="reviews-heading">A 5-star recipe app.</h2>
            <p>See what people are saying about Bite.</p>
          </ReviewsHeader>
          <ReviewCarousel reviews={appStoreReviews} />
        </ReviewsSection>

        <FinalCTA data-reveal data-reveal-direction="scale">
          <h2>
            Your recipes deserve
            <br />
            a better home.
          </h2>
          <CTAButton href={download.url} target="_blank" rel="noreferrer">
            Download Bite
          </CTAButton>
          <Proof>
            <Stars aria-label="5 stars">★★★★★</Stars>
            <span>Rated 5 stars on the App Store</span>
          </Proof>
        </FinalCTA>
      </main>
      <Footer>
        <FooterLinks aria-label="Footer navigation">
          <a href="/#features">Features</a>
          <a href="/#community">Community</a>
          <a href="/#collections">Collections</a>
          <a href="/connect">Claude &amp; ChatGPT</a>
          <a href="mailto:support@biterecipes.co">Contact</a>
          <a href="/privacy">Privacy</a>
          <a href="/terms">Terms</a>
        </FooterLinks>
      </Footer>
    </Page>
  );
}

const Page = styled.div`
  background: #ffffff;
  color: #080808;
  overflow: hidden;

  [data-reveal] {
    transition: opacity 850ms cubic-bezier(0.16, 1, 0.3, 1),
      transform 850ms cubic-bezier(0.16, 1, 0.3, 1);
    will-change: opacity, transform;
  }

  [data-reveal-delay="1"] {
    transition-delay: 160ms;
  }

  [data-reveal-delay="2"] {
    transition-delay: 280ms;
  }

  [data-reveal][data-community-scene] {
    transition: none;
  }

  /* Content starts hidden only once JavaScript is running (html.js is set
     in index.html), so pre-rendered HTML is fully visible on its own. */
  @media (prefers-reduced-motion: no-preference) {
    html.js & [data-reveal]:not(.is-visible):not([data-community-scene]) {
      opacity: 0;
      transform: translateY(56px);
    }

    html.js
      &
      [data-reveal][data-reveal-direction="left"]:not(.is-visible):not(
        [data-community-scene]
      ) {
      transform: translateX(-140px) rotate(-1.5deg);
    }

    html.js
      &
      [data-reveal][data-reveal-direction="right"]:not(.is-visible):not(
        [data-community-scene]
      ) {
      transform: translateX(140px) rotate(1.5deg);
    }

    html.js
      &
      [data-reveal][data-reveal-direction="scale"]:not(.is-visible):not(
        [data-community-scene]
      ) {
      transform: translateY(34px) scale(0.94);
    }
  }

  @media (prefers-reduced-motion: no-preference) and (max-width: 720px) {
    html.js
      &
      [data-reveal][data-reveal-direction="left"]:not(.is-visible):not(
        [data-community-scene]
      ) {
      transform: translateX(-64px) rotate(-1deg);
    }

    html.js
      &
      [data-reveal][data-reveal-direction="right"]:not(.is-visible):not(
        [data-community-scene]
      ) {
      transform: translateX(64px) rotate(1deg);
    }
  }

  h1,
  h2,
  p {
    margin: 0;
  }

  h1 {
    font-family: "Recoleta", Georgia, serif;
    font-size: clamp(4.1rem, 8vw, 7.5rem);
    line-height: 0.95;
    letter-spacing: 0;
    font-weight: 500;
  }

  h2 {
    font-family: "Recoleta", Georgia, serif;
    font-size: clamp(2.8rem, 5.2vw, 5.2rem);
    line-height: 1;
    letter-spacing: 0;
    font-weight: 500;
  }

  p {
    color: #687080;
    font-size: clamp(1.1rem, 1.7vw, 1.35rem);
    line-height: 1.35;
  }

  @media (max-width: 720px) {
    h1 {
      font-size: clamp(3rem, 15vw, 4.6rem);
    }

    h2 {
      font-size: clamp(2.35rem, 10vw, 3.5rem);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    [data-reveal] {
      transition: none;
    }
  }
`;

const HeroSection = styled.section`
  width: min(100% - 40px, 1280px);
  margin: 0 auto;
  padding: 62px 0 130px;

  @media (max-width: 720px) {
    padding: 46px 0 92px;
  }
`;

const HeroCopy = styled.div`
  display: grid;
  justify-items: center;
  gap: 28px;
  text-align: center;
  margin: 0 auto 36px;
`;

const HeroText = styled.p`
  max-width: 720px;
`;

const CTAButton = styled.a`
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 26px;
  border-radius: 999px;
  background: #050505;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 650;
  text-decoration: none;
  transition: transform 180ms ease, background-color 180ms ease;

  &:hover {
    background: #242424;
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 3px solid #8ab4ff;
    outline-offset: 4px;
  }
`;

const NewPill = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 14px 6px 6px;
  border: 1px solid #eceff2;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.04);
  color: #2a2f36;
  font-size: 0.92rem;
  font-weight: 600;
  text-decoration: none;
  transition: border-color 180ms ease, transform 180ms ease;

  b {
    padding: 3px 9px;
    border-radius: 999px;
    background: #12151a;
    color: #ffffff;
    font-size: 0.72rem;
    font-weight: 750;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  span {
    color: #8a909a;
    transition: transform 180ms ease;
  }

  &:hover {
    border-color: #d9dde2;
    transform: translateY(-1px);
  }

  &:hover span {
    transform: translateX(2px);
  }

  &:focus-visible {
    outline: 3px solid #8ab4ff;
    outline-offset: 4px;
  }

  @media (max-width: 420px) {
    font-size: 0.85rem;
  }
`;

const Proof = styled.div`
  display: grid;
  justify-items: center;
  gap: 4px;
  color: #0f1115;
  font-size: 0.98rem;

  small {
    color: #6d7480;
    font-size: 0.95rem;
  }
`;

const Stars = styled.span`
  color: #f5bd16;
  letter-spacing: 0;
`;

const RecipeCollage = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  grid-template-rows: repeat(5, 82px);
  grid-template-areas:
    "pancakes salad details muffins steak"
    "pancakes salad details muffins steak"
    "pancakes feed details dessert steak"
    "juice feed details dessert cafe"
    "juice feed details dessert cafe";
  gap: 8px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-template-rows: repeat(5, 96px);
    grid-template-areas:
      "pancakes salad details steak"
      "pancakes salad details steak"
      "pancakes feed details steak"
      "juice feed dessert cafe"
      "juice muffins dessert cafe";
    gap: 8px;
  }

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(7, 132px);
    grid-template-areas:
      "details details"
      "details details"
      "salad pancakes"
      "feed dessert"
      "muffins steak"
      "juice cafe"
      "juice cafe";
    gap: 6px;
  }
`;

const FoodCard = styled.div`
  grid-area: ${props => props.$area};
  min-height: 0;
  overflow: hidden;
  border-radius: 10px;
  background: #f4f4f4;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: 560px) {
    border-radius: 8px;
  }
`;

const FeatureSection = styled.section`
  width: ${props =>
    props.$wide ? "min(100% - 40px, 1280px)" : "min(100% - 40px, 1120px)"};
  margin: 0 auto;
  min-height: 100vh;
  min-height: 100svh;
  padding: 96px 0;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: ${props =>
    props.$wide ? "500px minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))"};
  gap: 80px;
  align-items: center;

  ${props => props.$reverse && "direction: rtl;"}

  > * {
    direction: ltr;
  }

  @media (max-width: 1050px) {
    grid-template-columns: 1fr;
    gap: 48px;
    padding: 84px 0;
  }
`;

const FeatureCopy = styled.div`
  display: grid;
  gap: 24px;
  max-width: 540px;

  h2 > span {
    white-space: nowrap;
  }
`;

const Eyebrow = styled.span`
  color: #7d8490;
  font-size: 0.82rem;
  font-weight: 750;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const SourceList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`;

const SourceIcon = styled.span`
  width: 38px;
  height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 180ms ease, opacity 180ms ease;

  &:hover {
    transform: translateY(-2px);
    opacity: 0.82;
  }

  &:focus-visible {
    outline: 3px solid #8ab4ff;
    outline-offset: 3px;
  }
`;

const SourceGlyph = styled.span`
  width: 28px;
  height: 28px;
  display: block;
  background: ${props => props.$color};
  mask: url(${props => props.$icon}) center / contain no-repeat;
  -webkit-mask: url(${props => props.$icon}) center / contain no-repeat;
`;

const SourceAppIcon = styled.img`
  width: ${props => props.$size || 32}px;
  height: ${props => props.$size || 32}px;
  display: block;
  object-fit: contain;
  border-radius: 7px;
`;

const TransformVisual = styled.div`
  display: grid;
  grid-template-columns: 190px 40px 310px;
  align-items: center;
  gap: 16px;
  justify-content: center;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
    justify-items: center;
  }
`;

const SourceCard = styled.div`
  width: min(100%, 190px);
  position: relative;
  border-radius: 14px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
  transform: rotate(-5deg);
  background: #ffffff;

  > img {
    width: 100%;
    aspect-ratio: 0.94;
    object-fit: cover;
    object-position: 50% 50%;
    display: block;
    border-radius: 14px 14px 0 0;
  }
`;

const InstagramBadge = styled.span`
  width: 40px;
  height: 40px;
  position: absolute;
  z-index: 1;
  top: -18px;
  left: 14px;
  display: grid;
  place-items: center;
  filter: drop-shadow(0 5px 8px rgba(0, 0, 0, 0.14));

  img {
    width: 100%;
    height: 100%;
    display: block;
    border-radius: 10px;
  }
`;

const SourceDetails = styled.div`
  min-height: 66px;
  padding: 13px 14px;
  box-sizing: border-box;
  display: grid;
  align-content: center;
  gap: 7px;
  border-radius: 0 0 14px 14px;

  span {
    height: 6px;
    border-radius: 999px;
    background: #dfe2e6;
  }

  span:nth-child(1) {
    width: 68%;
  }

  span:nth-child(2) {
    width: 88%;
  }

  span:nth-child(3) {
    width: 48%;
  }
`;

const Arrow = styled.img`
  width: 44px;
  height: 44px;
  display: block;
  transform: rotate(90deg);

  @media (max-width: 560px) {
    transform: rotate(180deg);
  }
`;

const RecipePreview = styled.div`
  width: min(100%, 330px);
  aspect-ratio: 0.489;
  border-radius: 26px;
  overflow: hidden;
  background: #ffffff;
  box-shadow: 0 18px 42px rgba(15, 23, 42, 0.1);

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    object-position: 50% 0%;
  }
`;

const CollectionGrid = styled.div`
  width: min(100%, 590px);
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 560px) {
    gap: 14px;
  }
`;

const CollectionCard = styled.article`
  min-width: 0;
  padding: 11px 11px 15px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.07);
  display: grid;
  gap: 5px;

  strong {
    margin-top: 5px;
    color: #20242a;
    font-size: 0.98rem;
    line-height: 1.2;
  }

  > span {
    color: #858b95;
    font-size: 0.82rem;
  }
`;

const CollectionImages = styled.div`
  aspect-ratio: 1.45;
  display: grid;
  grid-template-columns: 1.3fr 0.7fr;
  grid-template-rows: repeat(2, 1fr);
  gap: 4px;
  overflow: hidden;
  border-radius: 8px;

  img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    transition: opacity 700ms ease, transform 950ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  img:first-child {
    grid-row: 1 / -1;
  }

  img:nth-child(2) {
    transition-delay: 100ms;
  }

  img:nth-child(3) {
    transition-delay: 180ms;
  }

  @media (prefers-reduced-motion: no-preference) {
    html.js ${CollectionCard}:not(.is-visible) & img {
      opacity: 0;
    }

    html.js ${CollectionCard}:not(.is-visible) & img:first-child {
      transform: translateX(-24px) scale(0.94);
    }

    html.js ${CollectionCard}:not(.is-visible) & img:nth-child(2) {
      transform: translate(18px, -16px) scale(0.9);
    }

    html.js ${CollectionCard}:not(.is-visible) & img:nth-child(3) {
      transform: translate(18px, 16px) scale(0.9);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    img {
      transition: none;
    }
  }
`;

const CommunitySection = styled.section`
  width: min(100% - 40px, 1180px);
  margin: 0 auto;
  min-height: 100vh;
  min-height: 100svh;
  padding: 96px 0;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 0.78fr 1.1fr;
  gap: 64px;
  align-items: center;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: 44px;
    padding: 84px 0;
  }
`;

const CommunityCopy = styled(FeatureCopy)`
  h2 {
    font-size: clamp(2.7rem, 4.3vw, 4.4rem);
  }
`;

const drift = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const CommunityGrid = styled.div`
  position: relative;
  min-height: 620px;
  opacity: 1;
  transform: none;

  > article {
    transition: opacity 700ms cubic-bezier(0.16, 1, 0.3, 1),
      transform 700ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  > article:nth-of-type(1) {
    width: 286px;
    left: 50%;
    top: 88px;
    z-index: 3;
    transform: translateX(-50%) rotate(-1deg);
    transition-delay: 80ms;
  }

  > article:nth-of-type(2) {
    width: 168px;
    left: 8%;
    top: 48px;
    z-index: 1;
    transform: translate(18px, 12px) rotate(-3deg);
    transition-delay: 0ms;
  }

  > article:nth-of-type(3) {
    width: 164px;
    right: 6%;
    top: 318px;
    z-index: 2;
    transform: translate(-14px, -8px) rotate(3deg);
    transition-delay: 140ms;
  }

  @media (prefers-reduced-motion: no-preference) {
    html.js &:not(.is-visible) > article {
      opacity: 0;
      transform: translateY(28px) scale(0.97);
    }
  }

  @media (max-width: 560px) {
    min-height: 680px;

    > article:nth-of-type(1) {
      width: min(72vw, 286px);
      top: 110px;
    }

    > article:nth-of-type(2) {
      width: min(40vw, 156px);
      top: 36px;
      left: 4%;
    }

    > article:nth-of-type(3) {
      width: min(40vw, 152px);
      top: 400px;
      right: 4%;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    > article {
      transition: none;
    }
  }
`;

const CommunityCard = styled.article`
  position: absolute;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid #eceff2;
  background: #ffffff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);

  ${props =>
    !props.$featured &&
    `
      box-shadow: 0 3px 12px rgba(15, 23, 42, 0.03);
    `}
`;

const CommunityRecipeImage = styled.img`
  width: 100%;
  aspect-ratio: ${props => (props.$featured ? "1 / 1.08" : "1 / 1")};
  display: block;
  object-fit: cover;
`;

const CommunityCardBody = styled.div`
  padding: ${props => (props.$featured ? "14px" : "10px 11px 11px")};
  display: grid;
  gap: ${props => (props.$featured ? "7px" : "4px")};

  > strong {
    color: #20242a;
    font-size: ${props => (props.$featured ? "0.98rem" : "0.78rem")};
    line-height: 1.2;
  }

  > small {
    color: #858b95;
    font-size: 0.73rem;
    line-height: 1.25;
  }
`;

const Creator = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => (props.$featured ? "7px" : "5px")};

  img {
    width: ${props => (props.$featured ? "22px" : "16px")};
    height: ${props => (props.$featured ? "22px" : "16px")};
    border-radius: 50%;
    object-fit: cover;
    object-position: 50% 24%;
    opacity: ${props => (props.$featured ? 1 : 0.85)};
  }

  span {
    color: ${props => (props.$featured ? "#6a717c" : "#8a909a")};
    font-size: ${props => (props.$featured ? "0.72rem" : "0.64rem")};
    font-weight: 600;
  }
`;

const CommunityActivity = styled.div`
  position: absolute;
  inset: 0;
  z-index: 5;
  pointer-events: none;
`;

const ActivityBubble = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 11px 7px 7px;
  border: 1px solid rgba(229, 231, 235, 0.85);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 3px 12px rgba(15, 23, 42, 0.045);
  transition: opacity 650ms cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: ${props => 520 + props.$index * 280}ms;

  img {
    width: ${props => (props.$index === 0 ? 32 : 28)}px;
    height: ${props => (props.$index === 0 ? 32 : 28)}px;
    border-radius: 50%;
    object-fit: cover;
    object-position: 50% 22%;
  }

  span {
    display: grid;
    gap: 1px;
    color: #7b828d;
    font-size: 0.68rem;
    line-height: 1.1;
    white-space: nowrap;
  }

  strong {
    color: #30343a;
    font-size: 0.72rem;
  }

  @media (prefers-reduced-motion: no-preference) {
    html.js ${CommunityGrid}:not(.is-visible) & {
      opacity: 0;
    }

    ${CommunityGrid}.is-visible & {
      animation: ${drift} ${props => 4.8 + props.$index * 0.45}s ease-in-out
        ${props => props.$index * -0.7}s infinite;
    }
  }

  ${props =>
    props.$index === 0 &&
    `
      left: 42%;
      top: 22px;
    `}
  ${props =>
    props.$index === 1 &&
    `
      right: 2%;
      top: 128px;
    `}
  ${props =>
    props.$index === 2 &&
    `
      left: 2%;
      top: 430px;
    `}

  @media (max-width: 560px) {
    ${props =>
      props.$index === 0 &&
      `
        left: auto;
        right: 2%;
        top: 68px;
      `}
    ${props =>
      props.$index === 1 &&
      `
        right: 0;
        top: 310px;
      `}
    ${props =>
      props.$index === 2 &&
      `
        left: 0;
        top: 520px;
      `}
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    transition: none;
    animation: none;
  }
`;

const AssistantBadges = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;

  > span:not(:first-child) {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px 7px 10px;
    border: 1px solid #ececec;
    border-radius: 999px;
    background: #ffffff;
    color: #1f2329;
    font-size: 0.92rem;
    font-weight: 650;
  }
`;

const AssistantLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 20px;

  small {
    color: #8a909a;
    font-size: 0.92rem;
  }
`;

const TextLink = styled.a`
  color: #080808;
  font-size: 1.02rem;
  font-weight: 650;
  text-decoration: none;

  span {
    display: inline-block;
    transition: transform 180ms ease;
  }

  &:hover span {
    transform: translateX(3px);
  }

  &:focus-visible {
    outline: 3px solid #8ab4ff;
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

const AssistantPreview = styled(AssistantChatPreview)`
  width: 100%;
  max-width: 480px;
  justify-self: end;
  box-sizing: border-box;

  @media (max-width: 1050px) {
    justify-self: center;
  }
`;

const MoreSection = styled.section`
  width: min(100% - 40px, 1120px);
  margin: 0 auto;
  min-height: 100vh;
  min-height: 100svh;
  padding: 96px 0;
  box-sizing: border-box;
  display: grid;
  align-content: center;
  gap: 48px;

  @media (max-width: 720px) {
    padding: 84px 0;
    gap: 36px;
  }
`;

const MoreHeader = styled.div`
  display: grid;
  gap: 22px;
  width: 100%;
  text-align: center;
  justify-items: center;

  h2 {
    max-width: none;
  }

  > p {
    max-width: 52ch;
  }
`;

const MoreLayout = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 48px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const AskBitePanel = styled.div`
  padding: 32px 28px 28px;
  border: 1px solid #eeeeee;
  border-radius: 18px;
  background: linear-gradient(165deg, #fafafa 0%, #ffffff 48%, #f7f8fa 100%);
  display: grid;
  gap: 28px;
  align-content: start;
`;

const AskBiteCopy = styled.div`
  display: grid;
  gap: 10px;

  strong {
    color: #12151a;
    font-family: "Recoleta", Georgia, serif;
    font-size: clamp(1.55rem, 2.4vw, 2rem);
    font-weight: 500;
    line-height: 1.15;
  }

  p {
    max-width: 34ch;
    color: #687080;
    font-size: 1.05rem;
    line-height: 1.4;
  }
`;

const AskBiteLabel = styled.span`
  color: #7d8490;
  font-size: 0.78rem;
  font-weight: 750;
  letter-spacing: 0.12em;
  text-transform: uppercase;
`;

const AskChat = styled.div`
  display: grid;
  gap: 10px;
`;

const AskBubble = styled.div`
  max-width: ${props => (props.$from === "user" ? "88%" : "92%")};
  justify-self: ${props => (props.$from === "user" ? "end" : "start")};
  padding: 12px 14px;
  border-radius: ${props =>
    props.$from === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px"};
  background: ${props => (props.$from === "user" ? "#12151a" : "#ffffff")};
  color: ${props => (props.$from === "user" ? "#ffffff" : "#2a2f36")};
  border: ${props =>
    props.$from === "bite" ? "1px solid #eceff2" : "1px solid transparent"};
  box-shadow: ${props =>
    props.$from === "bite" ? "0 2px 8px rgba(15, 23, 42, 0.03)" : "none"};
  font-size: 0.92rem;
  line-height: 1.4;
`;

const MoreFeatureList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  align-content: center;
`;

const MoreFeature = styled.li`
  padding: 22px 0;
  border-top: 1px solid #eceff2;
  display: grid;
  gap: 6px;

  &:last-child {
    border-bottom: 1px solid #eceff2;
  }

  strong {
    color: #1c2128;
    font-size: 1.05rem;
    font-weight: 650;
  }

  span {
    color: #7d8490;
    font-size: 0.98rem;
    line-height: 1.4;
  }
`;

const ReviewsSection = styled.section`
  width: 100%;
  min-height: 100vh;
  min-height: 100svh;
  padding: 96px 0;
  box-sizing: border-box;
  display: grid;
  align-content: center;
  gap: 48px;

  @media (max-width: 720px) {
    padding: 84px 0;
    gap: 36px;
  }
`;

const ReviewsHeader = styled.div`
  width: min(100% - 40px, 1120px);
  margin: 0 auto;
  display: grid;
  gap: 22px;

  > p {
    max-width: 620px;
  }
`;

const ReviewMarquee = styled.div`
  width: 100%;
  overflow: hidden;
  cursor: grab;
  touch-action: pan-y;
  user-select: none;
  mask-image: linear-gradient(
    to right,
    transparent,
    #000 5%,
    #000 95%,
    transparent
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    #000 5%,
    #000 95%,
    transparent
  );

  &:active {
    cursor: grabbing;
  }

  @media (max-width: 720px) {
    mask-image: linear-gradient(
      to right,
      transparent,
      #000 3%,
      #000 97%,
      transparent
    );
    -webkit-mask-image: linear-gradient(
      to right,
      transparent,
      #000 3%,
      #000 97%,
      transparent
    );
  }
`;

const ReviewTrack = styled.div`
  display: flex;
  gap: 16px;
  width: max-content;
  padding: 4px 24px 8px;
  will-change: ${props => (props.$static ? "auto" : "transform")};

  ${props =>
    props.$static &&
    `
      width: 100%;
      max-width: 1120px;
      margin: 0 auto;
      padding: 4px 20px 8px;
      flex-wrap: wrap;
      justify-content: flex-start;
      will-change: auto;
    `}
`;

const ReviewCard = styled.figure`
  margin: 0;
  flex: 0 0 auto;
  width: min(300px, 74vw);
  min-height: 0;
  padding: 22px 20px 20px;
  border: 1px solid #eeeeee;
  border-radius: 14px;
  background: #ffffff;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.03);
  display: grid;
  gap: 12px;
  align-content: start;

  blockquote {
    margin: 0;
    color: #2a2f36;
    font-size: 0.98rem;
    line-height: 1.45;
    font-weight: 500;
  }

  figcaption {
    color: #8a909a;
    font-size: 0.82rem;
    font-weight: 600;
  }
`;

const FinalCTA = styled.section`
  width: min(100% - 40px, 980px);
  margin: 0 auto;
  min-height: 100vh;
  min-height: 100svh;
  padding: 96px 0;
  box-sizing: border-box;
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 28px;
  text-align: center;

  @media (max-width: 720px) {
    padding: 84px 0;
  }
`;

const Footer = styled.footer`
  width: min(100% - 40px, 1120px);
  margin: 0 auto;
  padding: 36px 0 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #080808;
`;

const FooterLinks = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 18px 28px;

  a {
    color: #596171;
    font-size: 0.95rem;
    font-weight: 600;
    text-decoration: none;
  }

  a:hover {
    color: #080808;
  }
`;

export default Home;
